import { isPrerenderUserAgent } from "util/prerenderUserAgents";
import { apiRoutes, buildApiAbsoluteUrl } from "util/apiRoutes";

type WikipediaParseApiResponse = {
    parse?: {
        categories?: Array<Record<string, unknown>>;
        externallinks?: Array<string>;
        properties?: Record<string, unknown>;
        text?: string;
    };
};

type WikipediaParseResult = {
    wiki_parse_first_img: string | null;
    wiki_parse_categories: Array<Record<string, unknown>>;
    wiki_parse_externallinks: Array<string>;
    wiki_parse_wikibase_shortdesc?: string;
};

/**
 * Wikipedia REST summary response shape.
 */
type WikipediaSummaryApiResponse = {
    title?: string;
    displaytitle?: string;
    titles?: { canonical?: string };
    extract?: string;
    description?: string | null;
    thumbnail?: { source?: string };
    pageid?: number;
    lang?: string;
    content_urls?: WikipediaContentUrls;
    timestamp?: string;
    type?: string;
};

/**
 * Wikipedia content URLs payload.
 */
export type WikipediaContentUrls = {
    mobile?: {
        page?: string;
    };
    desktop?: {
        page?: string;
    };
};

export type WikipediaSummary = {
    title: string;
    displayTitle: string;
    canonicalTitle: string;
    extract: string;
    description: string | null;
    thumbnail: string | null;
    pageId: number | null;
    lang: string | null;
    contentUrls: WikipediaContentUrls | null;
    timestamp: string | null;
    wikipediaUrl: string;
};

/**
 * Guard helper to check for non-null objects.
 * @param value - Unknown value to validate.
 * @returns True when value is a non-null object.
 */
const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null;
};


/**
 * Decide when to prefer backend Wikipedia requests.
 * @returns True when backend should handle Wikipedia requests.
 */
const shouldUseBackendForWikipedia = (): boolean => {
    return isPrerenderUserAgent();
};

/**
 * Resolve the backend API base URL with a safe fallback.
 * @returns Base URL string or empty string when unavailable.
 */
/**
 * Fetch a backend response for Wikipedia helpers.
 * @param path - Backend utilities path.
 * @param params - Query string parameters.
 * @returns Fetch Response instance.
 */
const fetchBackendResponse = async (path: string, params: Record<string, string>): Promise<Response> => {
    const url = buildApiAbsoluteUrl(path, params);
    const response = await fetch(url, { credentials: "include" });
    return response;
};

/**
 * Fetch Wikipedia summary through the backend.
 * @param issueTitle - Wikipedia page title or cname.
 * @returns Summary data or null when not found.
 */
const fetchWikipediaSummaryViaBackend = async (issueTitle: string): Promise<WikipediaSummary | null> => {
    const response = await fetchBackendResponse(apiRoutes.utilities.wikipediaSummary, {
        title: issueTitle
    });

    if (response.status === 404) {
        return null;
    }
    if (!response.ok) {
        throw new Error(`Backend Wikipedia summary error: ${response.status} ${response.statusText}`);
    }

    const data = (await response.json()) as WikipediaSummary | null;
    return data;
};

/**
 * Fetch Wikipedia parse metadata through the backend.
 * @param canonicalTitle - Wikipedia canonical title.
 * @param findImage - Whether to attempt image extraction.
 * @returns Parsed Wikipedia data.
 */
const fetchWikipediaParseViaBackend = async (
    canonicalTitle: string,
    findImage: boolean
): Promise<WikipediaParseResult> => {
    const response = await fetchBackendResponse(apiRoutes.utilities.wikipediaParse, {
        title: canonicalTitle,
        findImage: findImage ? "true" : "false"
    });

    if (!response.ok) {
        throw new Error(`Backend Wikipedia parse error: ${response.status} ${response.statusText}`);
    }

    const data = (await response.json()) as WikipediaParseResult;
    return data;
};

export const parseWikipediaData = async (
    canonicalTitle: string,
    findImage: boolean = true
): Promise<WikipediaParseResult> => {
    let url = `https://en.wikipedia.org/w/api.php?action=parse&format=json&formatversion=2&origin=*&page=${canonicalTitle}&prop=categories|externallinks|properties`;
    if (findImage === true) {
        url += `|text`;
    }
    
    try {
        if (shouldUseBackendForWikipedia()) {
            return await fetchWikipediaParseViaBackend(canonicalTitle, findImage);
        }

        const response = await fetch(url);
        if (response.status === 403) {
            return await fetchWikipediaParseViaBackend(canonicalTitle, findImage);
        }
        if (!response.ok) {
            throw new Error(`Wikipedia API error: ${response.status} ${response.statusText}`);
        }

        const data = (await response.json()) as WikipediaParseApiResponse;
        if (!isRecord(data) || !isRecord(data.parse)) {
            throw new Error("Unexpected Wikipedia parse response.");
        }

        if (!findImage) {
            return {
                wiki_parse_first_img: null,
                wiki_parse_categories: Array.isArray(data.parse.categories) ? data.parse.categories : [],
                wiki_parse_externallinks: Array.isArray(data.parse.externallinks) ? data.parse.externallinks : [],
                wiki_parse_wikibase_shortdesc: typeof data.parse.properties?.["wikibase-shortdesc"] === "string"
                    ? data.parse.properties["wikibase-shortdesc"]
                    : undefined
            };
        }

        // Define the type for our regex matches
        type WikiImageMatch = [
            string,      // Full match
            string,      // Image URL
            string      // Width
        ];

        // Find all image matches with width attribute - now matches both attribute orders
        const parseText = typeof data.parse.text === "string" ? data.parse.text : "";
        const allMatches = Array.from(
            parseText.matchAll(/(?:width="(\d+)"[^>]*src="(\/\/upload\.wikimedia\.org[^"]+)"|src="(\/\/upload\.wikimedia\.org[^"]+)"[^>]* width="(\d+)")/g)
        );

        // Only take the first two matches
        const firstTwoMatches = allMatches.slice(0, 2).map((match): WikiImageMatch => {
            // If width comes first (original pattern)
            if (match[1] && match[2]) {
                return [match[0], match[2], match[1]];
            }
            // If src comes first (new pattern)
            return [match[0], match[3], match[4]];
        });

        // Find the first image with width >= 50 from the first two matches
        const validImg = firstTwoMatches.find(match => {
            if (!match[2]) return false;
            const width = parseInt(match[2], 10);
            return !isNaN(width) && width >= 56;
        });

        const firstImgUrl = validImg 
            ? `https:${validImg[1]}` 
            : null;

        // Extract categories
        const categories = Array.isArray(data.parse.categories) ? data.parse.categories : [];

        // Extract external links
        const externalLinks = Array.isArray(data.parse.externallinks) ? data.parse.externallinks : [];

        // Extract wikibase-shortdesc
        const shortDesc = typeof data.parse.properties?.["wikibase-shortdesc"] === "string"
            ? data.parse.properties["wikibase-shortdesc"]
            : undefined;

        return {
            wiki_parse_first_img: firstImgUrl,
            wiki_parse_categories: categories,
            wiki_parse_externallinks: externalLinks,
            wiki_parse_wikibase_shortdesc: shortDesc
        };
    } catch (error) {
        console.error('Error parsing Wikipedia data:', error);
        return await fetchWikipediaParseViaBackend(canonicalTitle, findImage);
    }
};

/**
 * Converts a Wikipedia thumbnail URL to its full-resolution version
 * @param thumbnailUrl - The Wikipedia thumbnail URL
 * @returns The full-resolution image URL or null if invalid URL
 * 
 * Example:
 * Input: https://upload.wikimedia.org/wikipedia/commons/thumb/f/f9/Guy-Fawkes.jpg/320px-Guy-Fawkes.jpg
 * Output: https://upload.wikimedia.org/wikipedia/commons/f/f9/Guy-Fawkes.jpg
 */
export const getWikiFullResolutionUrl = (thumbnailUrl: string): string | null => {
    try {
        // Check if it's a valid Wikipedia Commons URL
        if (!thumbnailUrl.includes('upload.wikimedia.org/wikipedia/commons')) {
            return null;
        }

        // If it's not a thumbnail, return as is
        if (!thumbnailUrl.includes('/thumb/')) {
            return thumbnailUrl;
        }

        // Split the URL by '/'
        const urlParts = thumbnailUrl.split('/');
        
        // Remove 'thumb' from the path
        const thumbIndex = urlParts.indexOf('thumb');
        if (thumbIndex !== -1) {
            urlParts.splice(thumbIndex, 1);
        }

        // Remove the size specification (last part)
        urlParts.pop();

        // Rejoin the URL
        return urlParts.join('/');
    } catch (error) {
        console.error('Error processing Wikipedia image URL:', error);
        return null;
    }
};

/**
 * Retrieves Wikipedia page summary data for a given issue title.
 * Only accepts REST summary responses where type === "standard"; returns null otherwise
 * (e.g., drops "disambiguation", "redirect", or other non-standard types).
 *
 * @param issueTitle - The title/name of the issue (can be from Wikipedia or canonical title)
 * @returns Promise containing Wikipedia summary data or null if not found or not standard
 * 
 * Example:
 * Input: "Climate_change" or "Climate change"
 * Output: { title, extract, thumbnail, canonical_title, ... }
 */
export const getWikipediaSummary = async (issueTitle: string): Promise<WikipediaSummary | null> => {
    try {
        // Clean the title for processing
        const cleanTitle = issueTitle.replace(/_/g, ' ').trim();

        // Detect compound/influence issues using '~'. For these, query Wikipedia using
        // only the subject (first part) and override the extract with a generic phrase.
        const isCompound = cleanTitle.includes("~");
        let subjectTitle = cleanTitle;
        let customDescription: string | null = null;

        if (isCompound) {
            const parts = cleanTitle.split("~");
            const subject = (parts[0] ?? "").trim();
            const topic = parts.slice(1).join("~").trim();

            if (subject.length > 0 && topic.length > 0) {
                subjectTitle = subject;
                customDescription = `Influence of ${subject} on ${topic}`;
            } else {
                // If parts are invalid, fall back to treating it as a normal title
                subjectTitle = cleanTitle;
                customDescription = null;
            }
        }

        const encodedTitle = encodeURIComponent(subjectTitle);
        
        // Use Wikipedia REST API to get page summary
        if (shouldUseBackendForWikipedia()) {
            return await fetchWikipediaSummaryViaBackend(issueTitle);
        }

        const response = await fetch(
            `https://en.wikipedia.org/api/rest_v1/page/summary/${encodedTitle}`
        );
        
        if (response.status === 403) {
            return await fetchWikipediaSummaryViaBackend(issueTitle);
        }
        if (!response.ok) {
            if (response.status === 404) {
                console.warn(`Wikipedia page not found for title: ${issueTitle}`);
                return null;
            }
            throw new Error(`Wikipedia API error: ${response.status} ${response.statusText}`);
        }
        
        const data = (await response.json()) as WikipediaSummaryApiResponse;

        // Accept only standard pages; drop disambiguation/redirect/no-extract/etc.
        // See: https://en.wikipedia.org/api/rest_v1/#/Page%20content/get_page_summary__title_
        if (typeof data?.type === "string" && data.type !== "standard") {
            return null;
        }
        
        // Return structured summary data
        return {
            title: typeof data.title === "string" ? data.title : subjectTitle,
            displayTitle: typeof data.displaytitle === "string"
                ? data.displaytitle
                : (typeof data.title === "string" ? data.title : subjectTitle),
            canonicalTitle: typeof data.titles?.canonical === "string"
                ? data.titles.canonical
                : (typeof data.title === "string" ? data.title : subjectTitle),
            extract: typeof data.extract === "string" ? data.extract : "",
            description: customDescription !== null
                ? customDescription
                : (typeof data.description === "string" ? data.description : null),
            thumbnail: typeof data.thumbnail?.source === "string" ? data.thumbnail.source : null,
            pageId: typeof data.pageid === "number" ? data.pageid : null,
            lang: typeof data.lang === "string" ? data.lang : null,
            contentUrls: isRecord(data.content_urls)
                ? (data.content_urls as WikipediaContentUrls)
                : null,
            timestamp: typeof data.timestamp === "string" ? data.timestamp : null,
            wikipediaUrl: `https://en.wikipedia.org/wiki/${encodeURIComponent(
                typeof data.titles?.canonical === "string"
                    ? data.titles.canonical
                    : (typeof data.title === "string" ? data.title : subjectTitle)
            )}`
        };
    } catch (error) {
        console.error('Error fetching Wikipedia summary:', error);
        return await fetchWikipediaSummaryViaBackend(issueTitle);
    }
};

